/**
 * Shared data-fetching mixin for the Closure Analytics dashboard.
 *
 * Each panel that wants to fetch a backend slice imports
 * this, supplies the URL + query params, and gets back consistent loading/error handling plus
 * progress-bar wiring through the global EventLoading bus.
 */

import { Component, Vue } from "vue-property-decorator";

import buildUrl from "build-url";

import { eventLoading } from "@/components/sce/EventLoading";

@Component
export default class RcaEndpointData extends Vue {
    init: boolean = false;
    loading: boolean = false;
    error: string | null = null;
    data: any = [];

    /**
     * Fetch a JSON payload from a backend endpoint.
     *
     * @param url    Absolute URL (built via the endpoints module).
     * @param params Plain object of query params; falsy values are stripped so callers can pass
     *               optional filters without having to guard each one.
     */
    loadData(url: string, params?: Record<string, any>): Promise<any> {
        this.loading = true;
        this.error = null;
        eventLoading.startLoading();

        const queryParams: Record<string, any> = {};
        if (params) {
            for (const key of Object.keys(params)) {
                const value = params[key];
                if (value === undefined || value === null || value === "") {
                    continue;
                }
                queryParams[key] = value;
            }
        }

        // disableCSV serializes arrays as repeated params (?k=A&k=B) instead of CSV (?k=A,B);
        // Django's request.query_params.getlist expects the repeated form.
        return Vue.axios({
            url: buildUrl(url, { queryParams, disableCSV: true }),
        })
            .then((response: any) => {
                this.data = response.data;
                this.init = true;
                return response.data;
            })
            .catch((err: any) => {
                console.error("[RCA]", url, err);
                this.error =
                    err?.response?.data?.error ||
                    err?.message ||
                    "Failed to load data.";
                this.init = false;
                throw err;
            })
            .finally(() => {
                this.loading = false;
                eventLoading.stopLoading();
            });
    }
}
